import type { Player } from '@/types/player';
import type { LeagueSettings } from '@/types/trade';

export interface SleeperUser {
  user_id: string;
  username: string;
  display_name: string;
  avatar: string | null;
}

export interface SleeperLeague {
  league_id: string;
  name: string;
  season: string;
  total_rosters: number;
  status: 'pre_draft' | 'drafting' | 'in_season' | 'complete';
  scoring_settings: Record<string, any>;
  roster_positions: string[];
  settings?: {
    league_average_match?: number;
    playoff_week_start?: number;
    num_teams?: number;
    playoff_teams?: number;
    playoff_type?: number;
    daily_waivers?: number;
    taxi_years?: number;
    taxi_allow_vets?: number;
    taxi_slots?: number;
    taxi_deadline?: number;
    reserve_allow_sus?: number;
    reserve_allow_cov?: number;
    reserve_allow_out?: number;
    reserve_allow_na?: number;
    reserve_allow_doubtful?: number;
    reserve_slots?: number;
    leg?: number;
    offseason_adds?: number;
    bench_lock?: number;
    reserve_allow_dnr?: number;
    commissioner_direct_invite?: number;
    reserve_allow_ir?: number;
    waiver_type?: number;
    waiver_clear_days?: number;
    daily_waivers_last_ran?: number;
    waiver_day_of_week?: number;
    start_week?: number;
    playoff_seed_type?: number;
    daily_waivers_days?: number;
    max_keepers?: number;
    type?: number;
    pick_trading?: number;
    disable_adds?: number;
    waiver_budget?: number;
    disable_trades?: number;
    trade_review_days?: number;
    squad_streaming?: number;
    trade_deadline?: number;
    reserve_allow_pup?: number;
  };
}

export interface SleeperRoster {
  roster_id: number;
  owner_id: string;
  players: string[];
  starters: string[];
  settings: {
    wins: number;
    losses: number;
    ties: number;
    fpts: number;
    fpts_decimal: number;
  };
}

export interface SleeperLeagueUser {
  user_id: string;
  username: string;
  display_name: string;
  avatar: string | null;
  metadata?: {
    team_name?: string;
  };
}

// Get user by username or user_id
export async function getSleeperUser(usernameOrId: string): Promise<SleeperUser | null> {
  try {
    const response = await fetch(`https://api.sleeper.app/v1/user/${usernameOrId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Error fetching Sleeper user:', error);
    return null;
  }
}

// Get all leagues for a user
export async function getSleeperLeagues(userId: string, season: string = '2024'): Promise<SleeperLeague[]> {
  try {
    const response = await fetch(`https://api.sleeper.app/v1/user/${userId}/leagues/nfl/${season}`);
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('Error fetching Sleeper leagues:', error);
    return [];
  }
}

// Get rosters for a league
export async function getSleeperRosters(leagueId: string): Promise<SleeperRoster[]> {
  try {
    const response = await fetch(`https://api.sleeper.app/v1/league/${leagueId}/rosters`);
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('Error fetching Sleeper rosters:', error);
    return [];
  }
}

// Get users in a league
export async function getSleeperLeagueUsers(leagueId: string): Promise<SleeperLeagueUser[]> {
  try {
    const response = await fetch(`https://api.sleeper.app/v1/league/${leagueId}/users`);
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('Error fetching Sleeper league users:', error);
    return [];
  }
}

// Get all NFL players from Sleeper (use sparingly - call once per day max)
export async function getSleeperPlayers(): Promise<Record<string, any>> {
  try {
    const response = await fetch('https://api.sleeper.app/v1/players/nfl');
    if (!response.ok) return {};
    return await response.json();
  } catch (error) {
    console.error('Error fetching Sleeper players:', error);
    return {};
  }
}

// Find user's roster in a league
export async function getUserRosterInLeague(leagueId: string, userId: string): Promise<SleeperRoster | null> {
  try {
    const [rosters, users] = await Promise.all([
      getSleeperRosters(leagueId),
      getSleeperLeagueUsers(leagueId)
    ]);

    // Find the user in the league
    const leagueUser = users.find(u => u.user_id === userId);
    if (!leagueUser) return null;

    // Find their roster
    const userRoster = rosters.find(r => r.owner_id === userId);
    return userRoster || null;
  } catch (error) {
    console.error('Error finding user roster:', error);
    return null;
  }
}

// Convert Sleeper player IDs to our Player objects
export async function convertSleeperPlayersToFantasyFiesta(
  sleeperPlayerIds: string[],
  sleeperPlayersData: Record<string, any>,
  fantasyFiestaPlayers: Player[]
): Promise<Player[]> {
  const convertedPlayers: Player[] = [];

  for (const sleeperId of sleeperPlayerIds) {
    const sleeperPlayer = sleeperPlayersData[sleeperId];
    if (!sleeperPlayer) continue;

    // Try to find matching player in our system by name
    const matchingPlayer = fantasyFiestaPlayers.find(p => 
      p.name.toLowerCase() === `${sleeperPlayer.first_name} ${sleeperPlayer.last_name}`.toLowerCase()
    );

    if (matchingPlayer) {
      convertedPlayers.push(matchingPlayer);
    } else {
      // If we can't find the player, we could create a basic Player object
      // But for now, we'll skip players not in our system
      console.warn(`Player not found in Fantasy Fiesta: ${sleeperPlayer.first_name} ${sleeperPlayer.last_name}`);
    }
  }

  return convertedPlayers;
}

// Get detailed league information including scoring settings
export async function getSleeperLeagueDetails(leagueId: string): Promise<SleeperLeague | null> {
  try {
    const response = await fetch(`https://api.sleeper.app/v1/league/${leagueId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Error fetching Sleeper league details:', error);
    return null;
  }
}

// Convert Sleeper scoring settings to Fantasy Fiesta LeagueSettings format
export function convertSleeperScoringToFantasyFiesta(sleeperLeague: SleeperLeague): LeagueSettings {
  const { scoring_settings, roster_positions, settings } = sleeperLeague;
  
  // Default Fantasy Fiesta scoring settings
  const defaultScoring = {
    PPR: true,
    pointsPerReception: 1.0,
    pointsPerPassTD: 4.0,
    pointsPerCarry: 0.0,
    pointsPerRushTD: 6.0,
    pointsPerRushYard: 0.1,
    pointsPerRecTD: 6.0,
    pointsPerRecYard: 0.1,
    pointsPerPassYard: 0.04,
    pointsPerPassINT: -2.0,
    pointsPerFumble: -2.0,
    tePremiumPPR: 0.0,
  };

  // Map Sleeper scoring to Fantasy Fiesta format
  const scoring = {
    PPR: (scoring_settings.rec || 0) > 0,
    pointsPerReception: scoring_settings.rec || defaultScoring.pointsPerReception,
    pointsPerPassTD: scoring_settings.pass_td || defaultScoring.pointsPerPassTD,
    pointsPerCarry: scoring_settings.rush_att || 0, // Sleeper sometimes has points per carry
    pointsPerRushTD: scoring_settings.rush_td || defaultScoring.pointsPerRushTD,
    pointsPerRushYard: scoring_settings.rush_yd || defaultScoring.pointsPerRushYard,
    pointsPerRecTD: scoring_settings.rec_td || defaultScoring.pointsPerRecTD,
    pointsPerRecYard: scoring_settings.rec_yd || defaultScoring.pointsPerRecYard,
    pointsPerPassYard: scoring_settings.pass_yd || defaultScoring.pointsPerPassYard,
    pointsPerPassINT: scoring_settings.pass_int || defaultScoring.pointsPerPassINT,
    pointsPerFumble: scoring_settings.fum_lost || defaultScoring.pointsPerFumble,
    tePremiumPPR: scoring_settings.bonus_rec_te || 0, // TE premium scoring
    
    // Backward compatibility fields
    passingYards: scoring_settings.pass_yd || defaultScoring.pointsPerPassYard,
    passingTD: scoring_settings.pass_td || defaultScoring.pointsPerPassTD,
    passingINT: scoring_settings.pass_int || defaultScoring.pointsPerPassINT,
    rushingYards: scoring_settings.rush_yd || defaultScoring.pointsPerRushYard,
    rushingTD: scoring_settings.rush_td || defaultScoring.pointsPerRushTD,
    receivingYards: scoring_settings.rec_yd || defaultScoring.pointsPerRecYard,
    receivingTD: scoring_settings.rec_td || defaultScoring.pointsPerRecTD,
    fumble: scoring_settings.fum_lost || defaultScoring.pointsPerFumble,
  };

  // Count roster positions to build roster requirements
  const rosterCounts = roster_positions.reduce((acc, pos) => {
    acc[pos] = (acc[pos] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Map to Fantasy Fiesta roster format
  const roster = {
    QB: rosterCounts.QB || 1,
    RB: rosterCounts.RB || 2,
    WR: rosterCounts.WR || 2,
    TE: rosterCounts.TE || 1,
    FLEX: (rosterCounts.FLEX || 0) + (rosterCounts['W/R/T'] || 0) + (rosterCounts['R/W/T'] || 0),
    SUPERFLEX: (rosterCounts.SUPER_FLEX || 0) + (rosterCounts['Q/W/R/T'] || 0),
    K: rosterCounts.K || 1,
    DEF: (rosterCounts.DEF || 0) + (rosterCounts['D/ST'] || 0) || 1,
  };

  return {
    teamCount: sleeperLeague.total_rosters,
    superflex: roster.SUPERFLEX > 0,
    scoring,
    roster,
    consistencyWeight: 0.3, // Default consistency weight
  };
}

// Enhanced interface for import results that includes league settings
export interface SleeperImportResult {
  success: boolean;
  players: Player[];
  leagueSettings?: LeagueSettings;
  leagueName?: string;
  error?: string;
}

// Function to adjust player projections based on league scoring settings
export function adjustProjectionsForScoring(
  players: Player[],
  leagueSettings: LeagueSettings
): Player[] {
  return players.map(player => {
    const currentProjection = player.projection || 0;
    let adjustedProjection = currentProjection;

    // Calculate scoring multiplier based on position and league settings
    const scoring = leagueSettings.scoring;
    let scoringMultiplier = 1.0;

    switch (player.position) {
      case 'WR':
      case 'RB':
      case 'TE':
        // Receiving positions benefit from PPR scoring
        if (scoring.pointsPerReception > 0) {
          // Estimate receptions based on position and projection
          const estimatedReceptions = getEstimatedReceptions(player.position, currentProjection);
          const pprBonus = estimatedReceptions * scoring.pointsPerReception;
          
          // Standard leagues typically have 0 PPR, PPR leagues have 1 PPR, half-PPR has 0.5
          // Adjust projection by the difference from standard (no PPR)
          if (scoring.pointsPerReception > 0) {
            adjustedProjection += pprBonus * 0.8; // Conservative adjustment
          }
        }

        // TE premium scoring
        if (player.position === 'TE' && scoring.tePremiumPPR > 0) {
          const estimatedTEReceptions = getEstimatedReceptions('TE', currentProjection);
          adjustedProjection += estimatedTEReceptions * scoring.tePremiumPPR * 0.8;
        }
        break;

      case 'QB':
        // QB scoring adjustments
        const standardPassTD = 4; // Standard is 4 points per passing TD
        const standardPassYard = 0.04; // Standard is 25 yards per point (0.04 per yard)
        
        if (scoring.pointsPerPassTD !== standardPassTD) {
          const estimatedPassTDs = Math.max(15, Math.min(45, currentProjection / 8)); // Rough estimate
          const tdDifference = scoring.pointsPerPassTD - standardPassTD;
          adjustedProjection += estimatedPassTDs * tdDifference;
        }
        
        if (scoring.pointsPerPassYard !== standardPassYard) {
          const estimatedPassYards = Math.max(2500, Math.min(5500, currentProjection * 15)); // Rough estimate
          const yardDifference = scoring.pointsPerPassYard - standardPassYard;
          adjustedProjection += estimatedPassYards * yardDifference;
        }
        break;

      case 'K':
        // Kicker scoring is typically standard, minimal adjustments needed
        break;

      case 'DEF':
        // Defense scoring varies but projections are generally consistent
        break;
    }

    // Apply superflex bonus for QBs in superflex leagues
    if (player.position === 'QB' && leagueSettings.superflex) {
      scoringMultiplier *= 1.15; // QBs are more valuable in superflex
    }

    adjustedProjection *= scoringMultiplier;

    return {
      ...player,
      projection: Math.round(Math.max(0, adjustedProjection))
    };
  });
}

// Helper function to estimate receptions based on position and projection
function getEstimatedReceptions(position: string, projection: number): number {
  switch (position) {
    case 'WR':
      // WRs typically get 0.4-0.8 receptions per projected point
      return Math.max(20, Math.min(140, projection * 0.6));
    case 'RB':
      // RBs typically get 0.2-0.5 receptions per projected point
      return Math.max(10, Math.min(100, projection * 0.35));
    case 'TE':
      // TEs typically get 0.3-0.7 receptions per projected point
      return Math.max(15, Math.min(120, projection * 0.5));
    default:
      return 0;
  }
}

// Enhanced main function to import both roster and league settings
export async function importRosterAndSettingsFromSleeper(
  usernameOrId: string,
  leagueId: string,
  fantasyFiestaPlayers: Player[]
): Promise<SleeperImportResult> {
  try {
    // Get user info
    const user = await getSleeperUser(usernameOrId);
    if (!user) {
      return { success: false, players: [], error: 'Sleeper user not found' };
    }

    // Get league details (includes scoring settings)
    const leagueDetails = await getSleeperLeagueDetails(leagueId);
    if (!leagueDetails) {
      return { success: false, players: [], error: 'League not found or inaccessible' };
    }

    // Get their roster in the specified league
    const roster = await getUserRosterInLeague(leagueId, user.user_id);
    if (!roster) {
      return { success: false, players: [], error: 'User not found in this league or no roster data' };
    }

    // Get Sleeper players data (we might want to cache this)
    const sleeperPlayersData = await getSleeperPlayers();

    // Convert Sleeper player IDs to our Player objects
    const players = await convertSleeperPlayersToFantasyFiesta(
      roster.players,
      sleeperPlayersData,
      fantasyFiestaPlayers
    );

    // Convert Sleeper league settings to Fantasy Fiesta format
    const leagueSettings = convertSleeperScoringToFantasyFiesta(leagueDetails);

    // Adjust player projections based on the league's scoring settings
    const adjustedPlayers = adjustProjectionsForScoring(players, leagueSettings);

    return { 
      success: true, 
      players: adjustedPlayers, 
      leagueSettings,
      leagueName: leagueDetails.name
    };
  } catch (error) {
    console.error('Error importing roster and settings from Sleeper:', error);
    return { success: false, players: [], error: 'Failed to import roster and settings' };
  }
}

// Legacy function for backward compatibility
export async function importRosterFromSleeper(
  usernameOrId: string,
  leagueId: string,
  fantasyFiestaPlayers: Player[]
): Promise<{ success: boolean; players: Player[]; error?: string }> {
  const result = await importRosterAndSettingsFromSleeper(usernameOrId, leagueId, fantasyFiestaPlayers);
  return {
    success: result.success,
    players: result.players,
    error: result.error
  };
} 